import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Paperclip, Plus, Search, X, Eye, Edit, FileText, Table, CheckCircle, RefreshCcw, Filter, ChevronDown, ChevronUp, Download, Upload, FileSpreadsheet, FileOutput, List, LayoutList, LayoutGrid, Layers, Calendar, Link, XCircle, Clock } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { mapComptabiliteStatusOptions, useComptabiliteTableColumnVisibility, useComptabiliteListRowSelection, ComptabiliteBulkDeleteFilterAction, COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS } from './utils/comptabiliteListPageUtils';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
} from '../../products/saudi/components/QawaemTableSelection';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  getAnnexeNotes,
  createAnnexeNote,
  updateAnnexeNote,
  deleteAnnexeNote } from '../../shared/utils/comptabiliteService';
import { fetchCurrentFiscalYearContext, resolveFiscalYearLabel } from './utils/fiscalYearUtils';
import { CustomDatePicker, CustomSelect, CustomNumberInput } from '../../shared/components/custom';
import {
  EMPTY_ANNEXES_FORM,
  buildAnnexesPayload,
  recordToFormData } from './10-annexes-notes/annexesNotesPayload';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import ComptabiliteFiscalYearField from './components/ComptabiliteFiscalYearField';

const ANNEXES_LIST_COLUMNS = [
  { key: 'annexe_ref', labelKey: 'col_reference' },
  { key: 'titre_annexe', labelKey: 'col_titre' },
  { key: 'type_annexe', labelKey: 'col_type' },
  { key: 'exercice_fiscal', labelKey: 'col_exercice' },
  { key: 'statut_annexe', labelKey: 'col_statut' },
];

const AnnexesNotesPage = () => {
  const { t } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  const p = (key, opts) => t(`modules.comptabilite.pages.annexesSituationPage.${key}`, opts);

  const translateStatut = (val) => {
    const map = {
      Brouillon: p('status_brouillon'),
      Validé: p('status_valide'),
      Publié: p('status_publie') };
    return map[val] || p('status_brouillon');
  };

  const translateType = (val) => {
    const map = {
      'Note Explicative': p('type_note_explicative'),
      Tableau: p('type_tableau'),
      Graphique: p('type_graphique'),
      Document: p('type_document'),
      'Politique Comptable': p('type_politique_comptable') };
    return map[val] || val || p('na');
  };

  const translateCategory = (val) => {
    const map = {
      Immobilisations: p('category_immobilisations'),
      Stocks: p('category_stocks'),
      Créances: p('category_creances'),
      Dettes: p('category_dettes'),
      Capitaux: p('category_capitaux'),
      Résultat: p('category_resultat'),
      Engagements: p('category_engagements') };
    return map[val] || val || p('na');
  };

  const typeSelectOptions = useMemo(() => [
    { value: 'Note Explicative', label: p('type_note_explicative'), isDefault: true },
    { value: 'Tableau', label: p('type_tableau'), isDefault: true },
    { value: 'Graphique', label: p('type_graphique'), isDefault: true },
    { value: 'Document', label: p('type_document'), isDefault: true },
    { value: 'Politique Comptable', label: p('type_politique_comptable'), isDefault: true },
  ], [t]);

  const categorySelectOptions = useMemo(() => [
    { value: 'Immobilisations', label: p('category_immobilisations'), isDefault: true },
    { value: 'Stocks', label: p('category_stocks'), isDefault: true },
    { value: 'Créances', label: p('category_creances'), isDefault: true },
    { value: 'Dettes', label: p('category_dettes'), isDefault: true },
    { value: 'Capitaux', label: p('category_capitaux'), isDefault: true },
    { value: 'Résultat', label: p('category_resultat'), isDefault: true },
    { value: 'Engagements', label: p('category_engagements'), isDefault: true },
  ], [t]);

  const chartSelectOptions = useMemo(() => [
    { value: 'Bar', label: p('chart_bar'), isDefault: true },
    { value: 'Line', label: p('chart_line'), isDefault: true },
    { value: 'Pie', label: p('chart_pie'), isDefault: true },
    { value: 'Area', label: p('chart_area'), isDefault: true },
  ], [t]);

  const statusSelectOptions = useMemo(() => [
    { value: 'Brouillon', label: p('status_brouillon'), isDefault: true },
    { value: 'Validé', label: p('status_valide'), isDefault: true },
    { value: 'Publié', label: p('status_publie'), isDefault: true },
  ], [t]);

  const statusBadgeOptions = useMemo(() => mapComptabiliteStatusOptions([
    { value: 'Brouillon', labelKey: 'status_brouillon', icon: Clock, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
    { value: 'Validé', labelKey: 'status_valide', icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'Publié', labelKey: 'status_publie', icon: Eye, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  ], p), [t]);

  const TYPE_FILTER_MAP = {
    notes_explicatives: 'Note Explicative',
    tableaux: 'Tableau',
    graphiques: 'Graphique',
    documents: 'Document' };

  // ===== STATE MANAGEMENT =====
  const [annexes, setAnnexes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAnnexe, setSelectedAnnexe] = useState(null);
  const [viewingAnnexe, setViewingAnnexe] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Advanced Options
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_annexes');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  // Date range filter
  const [setDateRange] = useState({ start: null, end: null });
  
  // Dropdowns
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Selection & Sorting
  const { filters: columnFilters, setFilter: setColumnFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  const columnFilterRules = useMemo(() => [
    textColumnRule('annexe_ref', (row) => row.annexe_ref ?? ''),
    textColumnRule('titre_annexe', (row) => row.titre_annexe ?? ''),
    textColumnRule('exercice_fiscal', (row) => row.exercice_fiscal ?? ''),
  ], []);
  
  // Expanded sections for GPS-style collapsible form
  const [annexesSituationFormTab, setAnnexesSituationFormTab] = useState('annexeInfo');
  const annexesSituationFormTabs = useMemo(() => ([
    { id: 'annexeInfo', label: p('section_annexe_info'), icon: ChevronUp },
    { id: 'contentData', label: p('section_content_data'), icon: ChevronUp },
    { id: 'referencesValidation', label: p('section_validation_publish'), icon: ChevronUp }
  ]), [p]);

  
  const [formData, setFormData] = useState({ ...EMPTY_ANNEXES_FORM });
  const [fiscalYearLabel, setFiscalYearLabel] = useState('');

  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    loadAnnexes();
    fetchCurrentFiscalYearContext()
      .then(({ label }) => { if (label) setFiscalYearLabel(label); })
      .catch(() => {});
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Select handlers
  
  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => deleteAnnexeNote(id)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'الملحقات' }));
        setSelectedRecords([]);
        clearSelection();
        loadAnnexes();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('save_error', { message: error.message }));
      }
    }
  };

  const loadAnnexes = async () => {
    try {
      setLoading(true);
      const response = await getAnnexeNotes();
      setAnnexes(sortRecordsNewestFirst(Array.isArray(response) ? response : []));
    } catch (error) {
      console.error('Error loading annexes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = buildAnnexesPayload(formData);
      
      if (selectedAnnexe) {
        await updateAnnexeNote(selectedAnnexe.id, payload);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الملحق' }));
      } else {
        await createAnnexeNote(payload);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الملحق' }));
      }
      await loadAnnexes();
      resetForm();
    } catch (error) {
      console.error('Error saving annexe:', error);
      notifyError(showNotify, p('save_error', { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (annexe) => {
    setSelectedAnnexe(annexe);
    setFormData(recordToFormData(annexe));
    setAnnexesSituationFormTab('annexeInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        setLoading(true);
        await deleteAnnexeNote(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الملحق' }));
        await loadAnnexes();
      } catch (error) {
        console.error('Error deleting annexe:', error);
        notifyError(showNotify, p('save_error', { message: error.message }));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleView = (annexe) => {
    setViewingAnnexe(annexe);
    setShowViewModal(true);
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const annexe = annexes.find((a) => a.id === recordId);
      if (!annexe) return;
      await updateAnnexeNote(recordId, { ...annexe, statut_annexe: newStatus });
      await loadAnnexes();
    } catch (error) {
      console.error('Error updating annexe status:', error);
    }
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_ANNEXES_FORM });
    setSelectedAnnexe(null);
    setShowModal(false);
    setAnnexesSituationFormTab('annexeInfo');
  };

  const openCreateModal = () => {
    setSelectedAnnexe(null);
    setFormData({
      ...EMPTY_ANNEXES_FORM,
      exercice_fiscal: fiscalYearLabel || resolveFiscalYearLabel(),
    });
    setAnnexesSituationFormTab('annexeInfo');
    setShowModal(true);
  };

  // Stats
  const stats = {
    total: annexes.length,
    notesExplicatives: annexes.filter(a => a.type_annexe === 'Note Explicative').length,
    tableaux: annexes.filter(a => a.type_annexe === 'Tableau').length,
    validees: annexes.filter(a => a.statut_annexe === 'Validé').length,
    aPublier: annexes.filter(a => a.publication_requise && a.statut_annexe !== 'Publié').length
  };

  // Distribution data structure
  const annexeDistribution = {
    all_annexes: {
      title: p('dist_overview_title'),
      data: [
        { key: 'Note Explicative', name: p('type_notes_explicatives'), count: annexes.filter(a => a.type_annexe === 'Note Explicative').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { key: 'Tableau', name: p('type_tableaux'), count: annexes.filter(a => a.type_annexe === 'Tableau').length, color: "bg-gradient-to-r from-emerald-500 to-emerald-600" },
        { key: 'Graphique', name: p('type_graphiques'), count: annexes.filter(a => a.type_annexe === 'Graphique').length, color: "bg-gradient-to-r from-purple-500 to-purple-600" }
      ]
    },
    by_type: {
      title: p('dist_by_type_title'),
      data: [
        { key: 'Note Explicative', name: p('type_notes_explicatives'), count: annexes.filter(a => a.type_annexe === 'Note Explicative').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { key: 'Tableau', name: p('type_tableaux'), count: annexes.filter(a => a.type_annexe === 'Tableau').length, color: "bg-gradient-to-r from-emerald-500 to-emerald-600" },
        { key: 'Graphique', name: p('type_graphiques'), count: annexes.filter(a => a.type_annexe === 'Graphique').length, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { key: 'Document', name: p('type_documents'), count: annexes.filter(a => a.type_annexe === 'Document').length, color: "bg-gradient-to-r from-amber-500 to-amber-600" }
      ]
    },
    by_status: {
      title: p('dist_by_status_title'),
      data: [
        { key: 'Brouillon', name: p('status_brouillon'), count: annexes.filter(a => a.statut_annexe === 'Brouillon').length, color: "bg-gradient-to-r from-gray-500 to-gray-600" },
        { key: 'Validé', name: p('status_valide'), count: annexes.filter(a => a.statut_annexe === 'Validé').length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { key: 'Publié', name: p('status_publie'), count: annexes.filter(a => a.statut_annexe === 'Publié').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" }
      ]
    }
  };

  const distributionInfo = annexeDistribution[mainViewMode] || annexeDistribution.all_annexes;

  // Main view tabs
  const mainTabs = [
    { id: 'all_annexes', label: p('tab_overview'), icon: LayoutGrid },
    { id: 'by_type', label: p('tab_by_type'), icon: FileText },
    { id: 'by_status', label: p('tab_by_status'), icon: CheckCircle }
  ];

  const quickFilterButtons = [
    { id: 'all', label: p('filter_all'), icon: Layers },
    { id: 'Brouillon', label: p('filter_brouillon'), icon: Edit },
    { id: 'Validé', label: p('filter_validees'), icon: CheckCircle },
    { id: 'Publié', label: p('filter_publiees'), icon: Eye }
  ];

  const filteredAnnexes = useMemo(() => {
    const base = annexes.filter(annexe => {

      const matchesFilter = quickFilter === 'all' || annexe.statut_annexe === quickFilter;

      if (activeDistributionFilter) {
        if (mainViewMode === 'by_status') {
          if (annexe.statut_annexe !== activeDistributionFilter) return false;
        } else if (mainViewMode === 'by_type' || mainViewMode === 'all_annexes') {
          if (annexe.type_annexe !== activeDistributionFilter) return false;
        }
      }

      const matchesSearch = !searchTerm ||
        annexe.annexe_ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        annexe.titre_annexe?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        annexe.exercice_fiscal?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        annexe.type_annexe?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesFilter && matchesSearch;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [annexes, quickFilter, activeDistributionFilter, mainViewMode, searchTerm, columnFilters, columnFilterRules]);

  const {
    selectedRecords: selectedRecords,
    setSelectedRecords: setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord: handleSelectRecordBase,
    clearSelection,
  } = useComptabiliteListRowSelection(filteredAnnexes, (row) => row.id);
  const handleSelectRecord = handleSelectRecordBase;
  
  

  const {
    qawaemColumnDefs,
    handleToggleColumn,
    qawaemVisibleColumnCount,
    isColVisible,
    visibleDataColumnCount } = useComptabiliteTableColumnVisibility(ANNEXES_LIST_COLUMNS, p);
  const annexesTableColSpan = visibleDataColumnCount + 2;

  return (
    <div className={`space-y-6 employees-container`}>
      {/* Header */}
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <ComptabiliteBulkDeleteFilterAction
              count={selectedRecords.length}
              onClick={handleBulkDelete}
              label={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
              title={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
            />
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}
            <FacturationViewModeToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isLight={isDashboardLightMode}
              cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
              tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
            />
          </div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Paperclip className="h-8 w-8 text-violet-400" />
            {p('title')}
            <button onClick={loadAnnexes} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={openCreateModal}
            className="bg-gradient-to-r from-violet-500 to-violet-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-violet-600 hover:to-violet-800 transition-all duration-300 flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_annexe')}</span>
          </button>
        </div>
      </div>

      {/* Stats Cards - 5 Cards with Violet theme */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: p('stat_total'), value: stats.total, icon: Paperclip, color: 'from-violet-500 to-violet-700' },
          { label: p('stat_notes_explicatives'), value: stats.notesExplicatives, icon: FileText, color: 'from-blue-500 to-blue-600' },
          { label: p('stat_tableaux'), value: stats.tableaux, icon: Table, color: 'from-emerald-500 to-emerald-600' },
          { label: p('stat_validees'), value: stats.validees, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { label: p('stat_a_publier'), value: stats.aPublier, icon: Eye, color: 'from-amber-500 to-amber-600' }
        ].slice(0, 4).map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative group"
          >
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.label}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Advanced Options Section */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 space-y-6 overflow-hidden"
          >
            {/* Main View Mode Tabs */}
            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('main_view_label')}</h3>
              <div className="flex flex-wrap gap-2">
                {mainTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => { setMainViewMode(tab.id); setActiveDistributionFilter(null); }}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                      mainViewMode === tab.id
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Filters */}
            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('quick_filters_label')}</h3>
              <div className="flex flex-wrap gap-2">
                {quickFilterButtons.map(btn => (
                  <button
                    key={btn.id}
                    onClick={() => setQuickFilter(btn.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      quickFilter === btn.id
                        ? 'bg-violet-500/30 text-violet-200 border-2 border-violet-400/50'
                        : 'bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    <btn.icon className="h-4 w-4" />
                    {btn.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Distribution Overview */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white/80 text-sm font-semibold uppercase tracking-wider">{distributionInfo.title}</h3>
                {activeDistributionFilter && (
                  <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                    <XCircle size={14} />{p('reset_filter')}
                  </button>
                )}
              </div>
              <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                  <button key={item.key} onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.key ? null : item.key)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.key ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className={`font-medium ${activeDistributionFilter === item.key ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                      <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                      <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                        const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                        return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                      })()}%` }}></div>
                    </div>
                    <p className={`mt-2 text-sm ${activeDistributionFilter === item.key ? 'text-white/80' : 'text-white/60'}`}>{(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? p('percent_of_total', { percent: (item.count / total * 100).toFixed(1) }) : p('percent_of_total', { percent: 0 });
                    })()}</p>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {isColVisible('annexe_ref') ? (
                    <TableColumnHeaderFilter columnKey="annexe_ref" label={p('col_reference')} value={columnFilters.annexe_ref || ''} onChange={(v) => setColumnFilter('annexe_ref', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('titre_annexe') ? (
                    <TableColumnHeaderFilter columnKey="titre_annexe" label={p('col_titre')} value={columnFilters.titre_annexe || ''} onChange={(v) => setColumnFilter('titre_annexe', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('type_annexe') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_type')}</th>
                  ) : null}
                  {isColVisible('exercice_fiscal') ? (
                    <TableColumnHeaderFilter columnKey="exercice_fiscal" label={p('col_exercice')} value={columnFilters.exercice_fiscal || ''} onChange={(v) => setColumnFilter('exercice_fiscal', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('statut_annexe') ? (
                    <th className="px-6 py-4 text-center text-white font-semibold">{p('col_statut')}</th>
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  thClassName={COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredAnnexes.length > 0 ? (
                  filteredAnnexes.map((annexe, index) => (
                    <tr 
                      key={annexe.id}
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(annexe.id) ? 'bg-white/10' : ''}`}
                    >
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(annexe.id)}
                        onChange={() => handleSelectRecord(annexe.id)}
                      />
                      {isColVisible('annexe_ref') ? (
                      <td className="px-6 py-4"><span className="text-white font-semibold">{annexe.annexe_ref || p('na')}</span></td>
                      ) : null}
                      {isColVisible('titre_annexe') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{annexe.titre_annexe || p('no_title')}</span>
                          <span className="text-white/50 text-xs mt-0.5">{translateCategory(annexe.categorie_annexe) || p('no_category')}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('type_annexe') ? (
                      <td className="px-6 py-4"><span className="text-white/70">{translateType(annexe.type_annexe)}</span></td>
                      ) : null}
                      {isColVisible('exercice_fiscal') ? (
                      <td className="px-6 py-4"><span className="text-white/70">{annexe.exercice_fiscal || p('na')}</span></td>
                      ) : null}
                      {isColVisible('statut_annexe') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={annexe.statut_annexe || 'Brouillon'}
                            recordId={annexe.id}
                            options={statusBadgeOptions}
                            onChange={handleStatusChange}
                            title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                          />
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => handleView(annexe)}
                          onEdit={() => handleEdit(annexe)}
                          onDelete={() => handleDelete(annexe.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={annexesTableColSpan} className="text-center py-12 text-white/60">
                      <Paperclip size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_state')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Cards View */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredAnnexes.length > 0 ? (
              filteredAnnexes.map((annexe, index) => (
                <motion.div
                  key={annexe.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Paperclip size={20} className="text-violet-400" />
                          {annexe.titre_annexe || p('no_title')}
                        </h3>
                        <QawaemClickableStatusBadge
                          status={annexe.statut_annexe || 'Brouillon'}
                          recordId={annexe.id}
                          options={statusBadgeOptions}
                          onChange={handleStatusChange}
                          title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_general')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_reference')}:</span> {annexe.annexe_ref || p('na')}</div>
                            <div><span className="font-medium">{p('label_type')}:</span> {translateType(annexe.type_annexe)}</div>
                            <div><span className="font-medium">{p('label_categorie')}:</span> {translateCategory(annexe.categorie_annexe)}</div>
                            <div><span className="font-medium">{p('label_exercice')}:</span> {annexe.exercice_fiscal || p('na')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_description')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <p>{annexe.description_annexe || p('no_description')}</p>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_validation')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_daf')}:</span> {annexe.validation_daf ? p('yes') : p('no')}</div>
                            <div><span className="font-medium">{p('label_auditeur')}:</span> {annexe.validation_auditeur ? p('yes') : p('no')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_conformite')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_pcn')}:</span> {annexe.conformite_pcn ? p('yes') : p('no')}</div>
                            <div><span className="font-medium">{p('label_ifrs')}:</span> {annexe.conformite_ifrs ? p('yes') : p('no')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(annexe)}
                      onEdit={() => handleEdit(annexe)}
                      onDelete={() => handleDelete(annexe.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Paperclip size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_state')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal - GPS ReactDOM Portal with 4 Collapsible Sections */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'} flex items-center gap-2`}>
                <Paperclip className="h-6 w-6 text-violet-400" />
                {selectedAnnexe ? p('modal_edit') : p('modal_new')}
              </h2>
              <div className="flex items-center gap-3">
                <button 
                  type="submit" 
                  form="annexe-form"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-violet-700 text-white hover:from-violet-600 hover:to-violet-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-violet-500/30"
                >
                  <CheckCircle size={18} />
                  {selectedAnnexe ? p('btn_update') : p('btn_create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}><X size={24} /></button>
              </div>
            </div>
            <div className={`shrink-0 px-6 pt-3 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <div className="flex flex-wrap gap-1" role="tablist">
                {annexesSituationFormTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = annexesSituationFormTab === tab.id;
                  return (
                    <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setAnnexesSituationFormTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? (isDashboardLightMode ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px' : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px') : (isDashboardLightMode ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80' : 'text-white/55 hover:text-white hover:bg-white/5')}`}>
                      <TabIcon size={16} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="annexe-form" onSubmit={handleSubmit}>
                {annexesSituationFormTab === 'annexeInfo' && (
<div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
<ComptabiliteFiscalYearField
                            label={p('label_exercice_fiscal')}
                            value={formData.exercice_fiscal}
                            onChange={(val) => setFormData({ ...formData, exercice_fiscal: val })}
                            placeholder={p('placeholder_exercice')}
                            required
                          />
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_type_annexe')}</label>
                            <CustomSelect
                              value={formData.type_annexe}
                              onChange={(val) => setFormData({ ...formData, type_annexe: val })}
                              options={typeSelectOptions}
                              placeholder={p('placeholder_select_type')}
                              creatable
                              searchable
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_categorie')}</label>
                            <CustomSelect
                              value={formData.categorie_annexe}
                              onChange={(val) => setFormData({ ...formData, categorie_annexe: val })}
                              options={categorySelectOptions}
                              placeholder={p('placeholder_select_category')}
                              creatable
                              searchable
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_numero_annexe')}</label>
                            <CustomNumberInput
                              step={1}
                              value={formData.numero_annexe}
                              onChange={(v) => setFormData({ ...formData, numero_annexe: v })}
                              placeholder={p('placeholder_numero')}
                            />
                          </div>
                          <div className="">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_titre')} <span className="qawaem-required-mark">*</span></label>
                            <input
                              type="text"
                              required
                              value={formData.titre_annexe}
                              onChange={(e) => setFormData({ ...formData, titre_annexe: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_titre')}
                            />
                          </div>
                          <div className="">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_description')}</label>
                            <textarea
                              value={formData.description_annexe}
                              onChange={(e) => setFormData({ ...formData, description_annexe: e.target.value })}
                              rows={3}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_description')}
                            />
                          </div>
                  </div>
                </div>
                )}
                {annexesSituationFormTab === 'contentData' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
<div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_contenu_texte')}</label>
                            <textarea
                              value={formData.contenu_texte}
                              onChange={(e) => setFormData({ ...formData, contenu_texte: e.target.value })}
                              rows={6}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_contenu')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_graphique_type')}</label>
                            <CustomSelect
                              value={formData.graphique_type}
                              onChange={(val) => setFormData({ ...formData, graphique_type: val })}
                              options={chartSelectOptions}
                              placeholder={p('placeholder_select_chart')}
                              searchable
                            />
                          </div>
</div>
                )}
                {annexesSituationFormTab === 'referencesValidation' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <p className="text-white/50 text-sm italic md:col-span-3">{p('section_advanced_placeholder')}</p>
                    </div>
                    <div className="pt-6 border-t border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_statut')}</label>
                            <CustomSelect
                              value={formData.statut_annexe}
                              onChange={(val) => setFormData({ ...formData, statut_annexe: val })}
                              options={statusSelectOptions}
                              placeholder={p('placeholder_select_status')}
                              searchable
                            />
                          </div>
                          <div className="flex items-center gap-6 pt-6 md:col-span-2">
                            <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.validation_daf}
                                onChange={(e) => setFormData({ ...formData, validation_daf: e.target.checked })}
                                className="rounded border-white/20"
                              />
                              <span>{p('label_validation_daf')}</span>
                            </label>
                            <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.publication_requise}
                                onChange={(e) => setFormData({ ...formData, publication_requise: e.target.checked })}
                                className="rounded border-white/20"
                              />
                              <span>{p('label_publication_requise')}</span>
                            </label>
                          </div>
                          <div className="md:col-span-3">
                            <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.conformite_pcn}
                                onChange={(e) => setFormData({ ...formData, conformite_pcn: e.target.checked })}
                                className="rounded border-white/20"
                              />
                              <span>{p('label_conformite_pcn')}</span>
                            </label>
                          </div>
                          <div className="md:col-span-3">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_notes')}</label>
                            <textarea
                              value={formData.notes}
                              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                              rows={2}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_notes')}
                            />
                          </div>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {showViewModal && viewingAnnexe && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => { setShowViewModal(false); setViewingAnnexe(null); }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Paperclip className="h-6 w-6 text-violet-400" />
                {p('view_title')}
              </h2>
              <button onClick={() => setShowViewModal(false)} className="text-white/60 hover:text-white transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Section 1: Informations Générales */}
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Paperclip size={18} className="text-violet-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_general')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_reference')}</p>
                    <p className="text-white font-semibold">{viewingAnnexe.annexe_ref || p('na')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_exercice_fiscal_short')}</p>
                    <p className="text-white font-semibold">{viewingAnnexe.exercice_fiscal || p('na')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_type')}</p>
                    <p className="text-white font-semibold">{translateType(viewingAnnexe.type_annexe)}</p>
                  </div>
                  <div className="md:col-span-3">
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_titre_short')}</p>
                    <p className="text-white font-semibold">{viewingAnnexe.titre_annexe || p('na')}</p>
                  </div>
                  {viewingAnnexe.description_annexe && (
                    <div className="md:col-span-3">
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_description')}</p>
                      <p className="text-white/70 text-sm">{viewingAnnexe.description_annexe}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Section 2: Contenu */}
              {viewingAnnexe.contenu_texte && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <FileText size={18} className="text-amber-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_content')}</h3>
                  </div>
                  <p className="text-white/70 text-sm whitespace-pre-wrap">{viewingAnnexe.contenu_texte}</p>
                </div>
              )}

              {/* Section 3: Status & Validation */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <CheckCircle size={18} className="text-green-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_status_validation')}</h3>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_statut')}</p>
                      <span className={`inline-block px-3 py-1.5 rounded-full text-xs font-medium border ${
                        viewingAnnexe.statut_annexe === 'Publié' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                        viewingAnnexe.statut_annexe === 'Validé' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                        'bg-gray-500/20 text-gray-400 border-gray-500/30'
                      }`}>
                        {translateStatut(viewingAnnexe.statut_annexe || 'Brouillon')}
                      </span>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_validation_daf')}</p>
                      <p className="text-white font-semibold">{viewingAnnexe.validation_daf ? p('yes') : p('no')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_validation_auditeur')}</p>
                      <p className="text-white font-semibold">{viewingAnnexe.validation_auditeur ? p('yes') : p('no')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <CheckCircle size={18} className="text-violet-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_conformite')}</h3>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_conformite_pcn')}</p>
                      <p className="text-white font-semibold">{viewingAnnexe.conformite_pcn ? p('yes') : p('no')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_conformite_ifrs')}</p>
                      <p className="text-white font-semibold">{viewingAnnexe.conformite_ifrs ? p('yes') : p('no')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_conformite_reglementaire')}</p>
                      <p className="text-white font-semibold">{viewingAnnexe.conformite_reglementaire ? p('yes') : p('no')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => { setShowViewModal(false); setViewingAnnexe(null); }}
              className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10"
            >
              {p('btn_close')}
            </button>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default AnnexesNotesPage;

